import dayjs, { type Dayjs } from 'dayjs';

import { formatEnglishTime } from '@/common/utils/formatEnglishDateTime';

/**
 * Student oral-exam join window — must stay in sync with
 * `api/app/core/services/oral_exams/token_service.py` (JOIN_EARLY_MINUTES,
 * JOIN_LATE_GRACE_MINUTES).
 */
export const ORAL_EXAM_JOIN_EARLY_MINUTES = 15;
export const ORAL_EXAM_JOIN_LATE_GRACE_MINUTES = 5;
/** Recommended lobby arrival — keep in sync with `RECOMMENDED_ARRIVAL_MINUTES` in token_service.py */
export const ORAL_EXAM_RECOMMENDED_ARRIVAL_MINUTES = 10;

export type OralExamStudentJoinPhase =
  | 'too_early'
  | 'open'
  | 'started_locked'
  | 'missed';

export function oralExamJoinWindowOpens(slotStart: Dayjs): Dayjs {
  return slotStart.subtract(ORAL_EXAM_JOIN_EARLY_MINUTES, 'minute');
}

export function oralExamJoinWindowCloses(slotStart: Dayjs): Dayjs {
  return slotStart.add(ORAL_EXAM_JOIN_LATE_GRACE_MINUTES, 'minute');
}

/**
 * Resolve which join UX phase applies for a confirmed student booking.
 * When `bypassWindow` is true (dev: matches backend DISABLE_ORAL_EXAM_JOIN_WINDOW),
 * the window is always open until the slot ends. When `isAdmitted` is true (the
 * student was already admitted into the cohort/exam), the late-join cutoff is
 * skipped and they can still join/rejoin while the exam session is live — mirrors the
 * backend's `student_admitted` stay TTL in `_resolve_join_times`. Calendar slot
 * end is the join door for students who never entered, not the exam session clock.
 */
export function resolveOralExamStudentJoinPhase(
  slotStartIso: string,
  slotEndIso: string,
  now: Dayjs = dayjs(),
  options?: { bypassWindow?: boolean; isAdmitted?: boolean },
): OralExamStudentJoinPhase {
  const slotStart = dayjs(slotStartIso);
  const slotEnd = dayjs(slotEndIso);
  const opens = oralExamJoinWindowOpens(slotStart);
  const closes = oralExamJoinWindowCloses(slotStart);

  if (options?.bypassWindow) {
    if (now.isAfter(slotEnd)) return 'missed';
    return 'open';
  }

  if (now.isBefore(opens)) return 'too_early';
  if (now.isAfter(slotEnd) && !options?.isAdmitted) return 'missed';
  if (now.isBefore(closes) || options?.isAdmitted) return 'open';
  return 'started_locked';
}

/** True when the student should see the Join lobby CTA. */
export function canShowOralExamJoinLobby(
  slotStartIso: string,
  slotEndIso: string,
  now?: Dayjs,
  options?: { bypassWindow?: boolean; isAdmitted?: boolean },
): boolean {
  return resolveOralExamStudentJoinPhase(slotStartIso, slotEndIso, now, options) === 'open';
}

/**
 * Dev-only escape hatch: bypass the student join window (mirrors backend
 * `DISABLE_ORAL_EXAM_JOIN_WINDOW`). Set `EXPO_PUBLIC_DISABLE_ORAL_EXAM_JOIN_WINDOW=true`
 * in a local `.env` only. The direct `process.env.<NAME>` access is required so
 * babel's `transform-inline-environment-variables` inlines it at build time.
 *
 * The `__DEV__` guard makes this impossible in a production/release bundle
 * (compile-time `false`, dead-code-eliminated by Metro), so a stray build-env var
 * cannot reopen the join window in prod.
 */
export function isOralExamJoinWindowBypassEnabled(): boolean {
  return __DEV__ && process.env.EXPO_PUBLIC_DISABLE_ORAL_EXAM_JOIN_WINDOW === 'true';
}

/** True when the student join grace window has closed (T+5) for lecturer preflight. */
export function isOralExamLecturerJoinWindowClosed(
  slotStartIso: string,
  slotEndIso: string,
  now: Dayjs = dayjs(),
  options?: { bypassWindow?: boolean },
): boolean {
  const bypass = options?.bypassWindow ?? isOralExamJoinWindowBypassEnabled();
  const phase = resolveOralExamStudentJoinPhase(slotStartIso, slotEndIso, now, { bypassWindow: bypass });
  return phase === 'started_locked' || phase === 'missed';
}

/** Whole minutes until join closes (T+5); 0 when already closed. */
export function oralExamJoinWindowRemainingMinutes(
  slotStartIso: string,
  now: Dayjs = dayjs(),
): number {
  const closes = oralExamJoinWindowCloses(dayjs(slotStartIso));
  if (!now.isBefore(closes)) return 0;
  return Math.max(0, Math.ceil(closes.diff(now, 'minute', true)));
}

export type OralExamWaitingTimePhase = 'before_start' | 'during_grace' | 'after_grace';

/**
 * Where the clock stands against the scheduled start, for both the student
 * waiting page and the supervisor start gate. The grace story only applies
 * from the scheduled start until start + grace.
 */
export function resolveOralExamWaitingTimePhase(
  slotStartIso: string,
  now: Dayjs = dayjs(),
): OralExamWaitingTimePhase {
  const slotStart = dayjs(slotStartIso);
  if (now.isBefore(slotStart)) return 'before_start';
  if (now.isBefore(oralExamJoinWindowCloses(slotStart))) return 'during_grace';
  return 'after_grace';
}

/** English clock time when the join window closes (scheduled start + grace). */
export function formatOralExamJoinWindowCloseTime(slotStartIso: string): string {
  const closes = oralExamJoinWindowCloses(dayjs(slotStartIso));
  return formatEnglishTime(closes.toDate());
}
